import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Play, Cpu, ShieldCheck, Recycle, Gauge, Globe2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, RevealLines, stagger, item } from "../components/motion";
import { SectionLabel, BtnPrimary, BtnGhost, CtaSection } from "../components/common";
import { IMG, STATS, SOLUTIONS, PRODUCTS, BLOG_POSTS, HOME_VIDEO_BG } from "../data/site";

const featured = ["sd850", "pn160", "wd450"].map(
  (s) => PRODUCTS.find((p) => p.slug === s)
);

const chapters = [
  { icon: Cpu, title: "Engineering", text: "Every system is designed in-house by mechanical, process and control engineers who own the outcome end to end." },
  { icon: ShieldCheck, title: "Reliability", text: "AISI 316L construction, redundant safety instrumentation and validated cycles built to run for decades." },
  { icon: Gauge, title: "Automation", text: "Programmable controls document each phase in real time, removing operator error and guesswork." },
  { icon: Recycle, title: "Efficiency", text: "Energy and water recovery options reduce operating cost while shrinking environmental impact." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <>
      <Seo
        title="STERILTRON | Medical Waste Treatment & Sterilization Technologies"
        description="STERILTRON develops advanced medical waste treatment, sterilization and industrial processing solutions engineered for reliable international operations."
        path="/"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-white">
        <div className="absolute inset-0 bp-grid opacity-70" />
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={IMG.hero} alt="STERILTRON medical waste sterilization system in a modern facility" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 pt-32 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <SectionLabel>Advanced Industrial Technology</SectionLabel>
          </motion.div>
          <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter leading-[0.9] text-brand-950 max-w-5xl">
            <RevealLines lines={["Advanced Technology", "for a Cleaner Future"]} delay={0.15} />
          </h1>
          <Reveal delay={0.6} className="mt-8 max-w-xl text-slate-700 text-base md:text-lg leading-relaxed">
            STERILTRON develops advanced engineering solutions for medical waste treatment,
            sterilization and industrial processing applications worldwide.
          </Reveal>
          <Reveal delay={0.75} className="mt-10 flex flex-col sm:flex-row gap-4">
            <BtnPrimary to="/solutions" testid="hero-solutions-btn">Explore Our Solutions</BtnPrimary>
            <BtnGhost to="/products" testid="hero-products-btn">Discover Our Products</BtnGhost>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="py-10 md:py-12 px-6 md:px-8">
                <div className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">{s.value}</div>
                <div className="mt-2 text-xs md:text-sm uppercase tracking-widest text-brand-200">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <SectionLabel>About STERILTRON</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 text-balance">
              A Hungary-based engineering company operating internationally
            </h2>
            <div className="mt-8 space-y-5 text-slate-700 text-base md:text-lg leading-relaxed">
              <p>
                Headquartered in Rétság, Hungary, STERILTRON designs and manufactures
                sterilization and medical waste treatment systems for healthcare, industrial
                and public-sector clients across the globe.
              </p>
              <p>
                Our multidisciplinary teams combine precision mechanical engineering, process
                control and environmental science to deliver equipment that facilities can rely
                on for decades of continuous operation.
              </p>
            </div>
            <div className="mt-10">
              <BtnGhost to="/about" testid="about-more-btn">More About Us</BtnGhost>
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal className="relative">
              <div className="overflow-hidden border border-slate-200">
                <motion.img
                  src={IMG.aboutFacility}
                  alt="STERILTRON manufacturing facility in Rétság"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-brand-800 text-white p-6 md:p-8 max-w-[220px]">
                <div className="font-heading text-3xl font-extrabold">Rétság</div>
                <div className="text-xs uppercase tracking-widest text-brand-200 mt-1">Hungary — Europe</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-slate-200 bg-brand-50/50 py-6">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {["Precision Engineering", "Medical Waste Treatment", "Validated Sterilization", "Environmental Responsibility", "International Operations", "Advanced Automation"].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-800/80">
              {t}
              <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* SOLUTIONS */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Our Solutions</SectionLabel>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 max-w-2xl text-balance">
                Integrated systems for every stage of the waste stream
              </h2>
            </div>
            <BtnGhost to="/solutions" testid="solutions-all-btn">All Solutions</BtnGhost>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200"
          >
            {SOLUTIONS.map((s) => (
              <motion.div key={s.slug} variants={item}>
                <Link
                  to={`/solutions/${s.slug}`}
                  data-testid={`home-solution-${s.slug}`}
                  className="group block bg-white h-full hover:bg-brand-50/40 transition-colors"
                >
                  <div className="overflow-hidden">
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-8">
                    <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-950 group-hover:text-brand-700 transition-colors">{s.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.short}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800">
                      Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
            <div className="bg-brand-800 text-white p-8 flex flex-col justify-between">
              <Globe2 size={32} className="text-brand-200" />
              <div className="mt-10">
                <h3 className="font-heading text-xl font-semibold tracking-tight">Engineered to your facility</h3>
                <p className="mt-3 text-sm text-brand-100 leading-relaxed">Every solution is dimensioned to your capacity, utilities and regulatory needs.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-20 md:py-32 bg-brand-50/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Featured Products</SectionLabel>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 max-w-2xl text-balance">
                Precision equipment, engineered to endure
              </h2>
            </div>
            <BtnGhost to="/products" testid="products-all-btn">View All Products</BtnGhost>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`home-product-${p.slug}`}
                  className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full"
                >
                  <div className="overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-8">
                    <div className="text-xs uppercase tracking-widest text-brand-600 font-semibold">{p.category}</div>
                    <h3 className="mt-3 font-heading text-lg font-semibold tracking-tight text-brand-950 leading-snug">{p.name}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.tagline}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 group-hover:gap-2.5 transition-all">
                      View Product <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGINEERING & TECHNOLOGY */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <SectionLabel>Engineering &amp; Technology</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 text-balance">
              A relentless focus on precision, safety and uptime
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {chapters.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="bg-white p-8 md:p-12">
                <div className="flex items-start justify-between">
                  <c.icon size={30} className="text-brand-800" strokeWidth={1.5} />
                  <span className="font-mono text-sm text-slate-400">0{i + 1}</span>
                </div>
                <h3 className="mt-8 font-heading text-2xl font-semibold tracking-tight text-brand-950">{c.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{c.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL SOLUTIONS */}
      <section className="relative overflow-hidden bg-brand-950 text-white">
        <img src={IMG.globalMap} alt="STERILTRON international operations map" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="max-w-2xl">
            <SectionLabel dark>Global Solutions</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-balance">
              Engineered for international projects
            </h2>
            <p className="mt-6 text-brand-100 text-base md:text-lg leading-relaxed">
              STERILTRON systems are designed to meet diverse operational requirements and
              regulatory frameworks, supporting turnkey projects and long-term service across
              multiple continents.
            </p>
            <div className="mt-10">
              <BtnGhost to="/projects" testid="global-projects-btn" dark>Explore Projects</BtnGhost>
            </div>
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal className="overflow-hidden border border-slate-200">
              <img src={IMG.sustainability} alt="Environmental sustainability in medical waste treatment" className="w-full h-[420px] md:h-[500px] object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionLabel>Sustainability</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 text-balance">
              Safer waste, lighter footprint
            </h2>
            <div className="mt-8 space-y-5 text-slate-700 text-base md:text-lg leading-relaxed">
              <p>
                Modern on-site treatment technology allows medical waste to be neutralised where
                it is generated — reducing the transport of hazardous material and the emissions
                that come with it.
              </p>
              <p>
                By combining validated sterilization with volume reduction and energy recovery,
                STERILTRON systems help facilities manage waste more safely while lowering their
                overall environmental impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DISCOVER OUR TECHNOLOGY */}
      <section className="relative overflow-hidden">
        <Link to="/videos" data-testid="home-video-hero" className="group block relative">
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden bg-brand-950">
            <img src={HOME_VIDEO_BG} alt="See STERILTRON technology in action" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-[1200ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-brand-950/40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-white/50 flex items-center justify-center backdrop-blur-sm bg-white/5 group-hover:scale-110 group-hover:bg-white/10 transition-all duration-300">
                <Play size={34} className="text-white ml-1" fill="currentColor" />
              </span>
              <span className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-brand-200">Discover Our Technology</span>
              <h2 className="mt-4 font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl text-balance">See STERILTRON Technology in Action</h2>
              <p className="mt-5 max-w-xl text-brand-100 text-sm md:text-base leading-relaxed">
                Discover how advanced engineering, automation and sterilization technology come together in modern waste treatment systems.
              </p>
              <span className="mt-8 inline-flex items-center gap-2 bg-white text-brand-900 px-8 py-4 text-sm font-semibold uppercase tracking-wider group-hover:bg-brand-100 transition-colors">
                Explore All Videos <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* LATEST INSIGHTS */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Latest Insights</SectionLabel>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl font-bold tracking-tighter text-brand-950 max-w-2xl text-balance">
                Perspectives from our engineering team
              </h2>
            </div>
            <BtnGhost to="/blog" testid="home-view-all-articles">View All Articles</BtnGhost>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/blog/${p.slug}`} data-testid={`home-blog-${p.slug}`} className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full">
                  <div className="overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-widest">
                      <span className="text-brand-700 font-semibold">{p.category}</span>
                      <span className="text-slate-300">/</span>
                      <span className="text-slate-500">{p.date}</span>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight text-brand-950 leading-snug">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 group-hover:gap-2.5 transition-all">Read More <ArrowRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}